/**
 * Сериализация состояния армянского белота для отладочного UI / экспорта логов.
 * Функции `rng` и прочие не-JSON значения заменяются строками-плейсхолдерами.
 *
 * @module game/belotArmenian/debugSerialize
 */

/**
 * @param {unknown} value
 * @returns {unknown}
 */
function safeJsonClone(value) {
  try {
    return JSON.parse(
      JSON.stringify(value, (_, v) => {
        if (typeof v === 'function') return '[Function]';
        if (typeof v === 'bigint') return String(v);
        return v;
      }),
    );
  } catch {
    return { _serializeError: true, hint: typeof value };
  }
}

/** Короткие подписи комбинаций для отладочного лога (как в UI капель). */
const COMBO_HEAD_RU = {
  TERZ: 'Терц',
  FIFTY: '50',
  HUNDRED: '100',
  CARRE_J: '4J',
  CARRE_9: '49',
  CARRE_A: '4A',
  CARRE_10: '4X',
  CARRE_K: '4K',
  CARRE_Q: '4Q',
};

/**
 * @param {unknown[]} keys
 * @returns {string}
 */
function declaredKeysSummaryRu(keys) {
  if (!Array.isArray(keys)) return '';
  return keys
    .map((k) => {
      const head = String(k || '').split(':')[0];
      return COMBO_HEAD_RU[head] || head || '?';
    })
    .filter(Boolean)
    .join(', ');
}

/**
 * @param {unknown} state — объект состояния из {@link ./engine}
 * @returns {Record<string, unknown>|null}
 */
function serializeArmenianEngineStateForLog(state) {
  if (!state || typeof state !== 'object') return null;
  const base = /** @type {Record<string, unknown>} */ (safeJsonClone(state));
  if (!base || typeof base !== 'object') return base;
  const rm = /** @type {{ seed?: number, mulberryA?: number, draws?: number } | null | undefined} */ (
    /** @type {{ rngMeta?: unknown }} */ (state).rngMeta
  );
  if (rm && typeof rm === 'object' && typeof rm.seed === 'number') {
    base.rng = {
      algorithm: 'mulberry32',
      seed: rm.seed >>> 0,
      mulberryState: Number(rm.mulberryA) | 0,
      draws: Number(rm.draws) || 0,
    };
  } else {
    base.rng = '[Function]';
  }
  delete base.rngMeta;
  return base;
}

/**
 * @param {unknown} cmd
 * @returns {unknown}
 */
function serializeCommandForLog(cmd) {
  const base = safeJsonClone(cmd);
  if (!cmd || typeof cmd !== 'object') return base;
  if (cmd.type === 'COMMIT_FIRST_PLAY_DECLARATIONS' && cmd.payload && typeof cmd.payload === 'object') {
    const keys = cmd.payload.declaredKeys;
    const summary = declaredKeysSummaryRu(keys);
    if (summary && base && typeof base === 'object' && !Array.isArray(base)) {
      /** @type {Record<string, unknown>} */ (base)._debug_decl_summary_ru = summary;
    }
  }
  if (cmd.type === 'PLAY_CARD' && cmd.payload && typeof cmd.payload === 'object') {
    const pk = cmd.payload.commitDeclaredKeys;
    if (Array.isArray(pk) && base && typeof base === 'object' && !Array.isArray(base)) {
      const summary = declaredKeysSummaryRu(pk);
      if (summary) /** @type {Record<string, unknown>} */ (base)._debug_decl_summary_ru = summary;
      else if (pk.length === 0) /** @type {Record<string, unknown>} */ (base)._debug_decl_summary_ru = '— (скрыто)';
    }
  }
  return base;
}

/**
 * @param {unknown[]} events
 * @returns {unknown[]}
 */
function serializeEventsForLog(events) {
  if (!Array.isArray(events)) return [];
  return /** @type {unknown[]} */ (safeJsonClone(events));
}

/**
 * Компактные подписи событий для строки истории (тип + ключи payload).
 *
 * @param {unknown[]} events
 * @returns {Array<{ type: string, payloadKeys: string[] }>}
 */
function serializeEventSummariesForLog(events) {
  if (!Array.isArray(events)) return [];
  return events.map((ev) => {
    if (!ev || typeof ev !== 'object') return { type: '?', payloadKeys: [] };
    const type = String(/** @type {{ type?: unknown }} */ (ev).type || '?');
    const payload = /** @type {{ payload?: unknown }} */ (ev).payload;
    let payloadKeys = [];
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      payloadKeys = Object.keys(/** @type {object} */ (payload));
    } else if (payload != null) {
      payloadKeys = ['[non-object]'];
    }
    return { type, payloadKeys };
  });
}

module.exports = {
  safeJsonClone,
  serializeArmenianEngineStateForLog,
  serializeCommandForLog,
  serializeEventsForLog,
  serializeEventSummariesForLog,
};
